// AI assisted development
/**
 * FeatureFlaggedRoute Component
 * 
 * Wraps a route element with feature flag protection.
 * Redirects to home if the feature is not enabled.
 */

import { Navigate } from 'react-router-dom';
import { useFeatureFlags } from '../hooks/useFeatureFlags';
import LoadingSpinner from './LoadingSpinner';

interface FeatureFlaggedRouteProps {
  /** The feature flag name to check */
  flagName: string;
  /** Default value if flag not found (default: false for safety) */
  defaultEnabled?: boolean;
  /** The element to render if feature is enabled */
  children: React.ReactNode;
  /** Path to redirect to if feature is disabled (default: '/') */
  redirectTo?: string;
}

/**
 * Protects a route behind a feature flag.
 * Shows loading spinner while checking, redirects if disabled.
 * 
 * @example
 * <Route 
 *   path="/feature" 
 *   element={
 *     <FeatureFlaggedRoute flagName="ENABLE_FEATURE">
 *       <FeaturePage />
 *     </FeatureFlaggedRoute>
 *   } 
 * />
 */
export default function FeatureFlaggedRoute({
  flagName,
  defaultEnabled = false,
  children,
  redirectTo = '/',
}: FeatureFlaggedRouteProps) {
  const { isEnabled, isLoading } = useFeatureFlags();

  // Show loading while checking flag
  if (isLoading) {
    return <LoadingSpinner message="Checking access..." />;
  }

  // Check if feature is enabled
  if (!isEnabled(flagName, defaultEnabled)) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

